import type { ConfiguratorDefinition } from "~/Configurators/common/v2/StepDefinition";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { RESET_SELECTION, RESET_SELECTION_AND_LENGTH } from "~/Configurators/common/advancedTranslationUtils";
import { getCsticsValue } from "~/Configurators/common/v2/helper";
import {
  SHAFT_MACHINING_LEFT,
  SHAFT_MACHINING_MIDDLE,
  SHAFT_MACHINING_RIGHT,
} from "~/Configurators/Shaft/inch/inchCsticConstants";
import { userIsCustomer } from "~/utils/roles";

export const ImperialShaftConfiguratorDefinition: ConfiguratorDefinition = {
  description: "configurator.shaft.step.1.substep.1.tile.1.description.inch",
  mediaUrl: [
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle, shaftLeft, shaftRight] = getCsticsValue(
          mainKmat,
          SHAFT_MACHINING_MIDDLE,
          SHAFT_MACHINING_LEFT,
          SHAFT_MACHINING_RIGHT,
        );
        return shaftMiddle === "980" && (["003", "007"].includes(shaftLeft) || ["003", "007"].includes(shaftRight));
      },
      value: "configurator.shaft.productImage.W.axialRadialThread",
    },
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle, shaftLeft, shaftRight] = getCsticsValue(
          mainKmat,
          SHAFT_MACHINING_MIDDLE,
          SHAFT_MACHINING_LEFT,
          SHAFT_MACHINING_RIGHT,
        );
        return shaftMiddle === "900" && (["003", "007"].includes(shaftLeft) || ["003", "007"].includes(shaftRight));
      },
      value: "configurator.shaft.productImage.W.axialThread",
    },
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle] = getCsticsValue(mainKmat, SHAFT_MACHINING_MIDDLE);
        return shaftMiddle === "980";
      },
      value: "configurator.shaft.productImage.W.radialThread",
    },
    {
      condition: ({ mainKmat }) => {
        const [shaftMiddle] = getCsticsValue(mainKmat, SHAFT_MACHINING_MIDDLE);
        return shaftMiddle === "900";
      },
      value: "configurator.shaft.productImage.W.noHoles",
    },
  ],
  pages: [
    {
      name: "configurator.shaft.step.1.title",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.Series,

          name: "configurator.shaft.step.1.substep.1.title",
          description: "configurator.shaft.step.1.substep.1.description",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.1.tile.1.title.inch",
              description: "configurator.shaft.step.1.substep.1.tile.1.description.inch",
              imageUrl: "configurator.shaft.step.1.substep.1.tile.1.image.inch",
              info: "configurator.shaft.step.1.substep.1.tile.1.infoPopup.inch",
              cstics: [
                {
                  csticName: "VMKB_078",
                  value: "WZ",
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 6,
          csticsToReset: ["VMKB_078"],
        },
        {
          type: SubstepType.Diameter,

          name: "configurator.shaft.step.1.substep.2.title.inch",
          description: "configurator.shaft.step.1.substep.2.description",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.2.tile.1.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1/4",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.2.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "3/8",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.3.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1/2",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.4.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "5/8",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.5.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "3/4",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.6.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "7/8",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.7.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.8.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1-1/8",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.9.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1-1/4",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.10.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1-3/8",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.11.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1-1/2",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.12.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "1-3/4",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.13.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "2",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.14.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "2-1/2",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.2.tile.15.title.inch",
              cstics: [
                {
                  csticName: "VBGR_078",
                  value: "3",
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 8,
          csticsToReset: ["VBGR_078"],
        },
        {
          type: SubstepType.Material,

          name: "configurator.shaft.step.1.substep.3.title",
          description: "configurator.shaft.step.1.substep.3.description",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.3.tile.1.title",
              info: "configurator.shaft.step.1.substep.3.tile.1.infoPopup",
              cstics: [
                {
                  csticName: "VWST_078",
                  value: "CF53",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.3.tile.2.title",
              info: "configurator.shaft.step.1.substep.3.tile.2.infoPopup",
              cstics: [
                {
                  csticName: "VWST_078",
                  value: "X46",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.3.tile.3.title",
              info: "configurator.shaft.step.1.substep.3.tile.3.infoPopup",
              cstics: [
                {
                  csticName: "VWST_078",
                  value: "X90",
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 4,
          csticsToReset: ["VWST_078"],
        },
        {
          type: SubstepType.Coating,

          name: "configurator.shaft.step.1.substep.4.title",
          description: "configurator.shaft.step.1.substep.4.description",
          info: "configurator.shaft.step.1.substep.4.infoPopup",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.4.tile.1.title",
              cstics: [
                {
                  csticName: "VBSCH_078",
                  value: "/",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.4.tile.2.title",
              description: "configurator.shaft.step.1.substep.4.tile.2.description",
              info: "configurator.shaft.step.1.substep.4.tile.2.infoPopup",
              cstics: [
                {
                  csticName: "VBSCH_078",
                  value: "CR",
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 4,
          csticsToReset: ["VBSCH_078"],
        },
        {
          type: SubstepType.Tolerance,

          name: "configurator.shaft.step.1.substep.5.title",
          description: "configurator.shaft.step.1.substep.5.description",
          info: "configurator.shaft.step.1.substep.5.infoPopup.inch",

          tiles: [
            {
              name: "configurator.shaft.step.1.substep.5.tile.1.title.inch",
              info: "configurator.shaft.step.1.substep.5.tile.1.infoPopup.inch",
              cstics: [
                {
                  csticName: "VTOL_078",
                  value: "L",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.5.tile.2.title.inch",
              info: "configurator.shaft.step.1.substep.5.tile.2.infoPopup.inch",
              cstics: [
                {
                  csticName: "VTOL_078",
                  value: "S",
                },
              ],
            },
            {
              name: "configurator.shaft.step.1.substep.5.tile.3.title.inch",
              info: "configurator.shaft.step.1.substep.5.tile.3.infoPopup.inch",
              cstics: [
                {
                  csticName: "VTOL_078",
                  value: "N",
                },
              ],
            },
          ],
          visible: true,

          tilesPerRow: 6,
          csticsToReset: ["VTOL_078"],
        },
      ],
    },
    {
      name: "configurator.shaft.step.2.title",
      enabled: true,
      resetLabel: RESET_SELECTION_AND_LENGTH,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.ShaftLength,
          name: "configurator.shaft.shaftLength.title.default",

          tiles: [],

          csticsToReset: [],
          visible: true,
        },
        {
          type: SubstepType.ShaftTolerance,
          name: "configurator.shaft.step.2.substep.2.title.inch",

          tiles: [
            {
              name: "configurator.shaft.step.2.substep.2.tile.1.title",

              cstics: [
                {
                  csticName: "VLMAX_01A_078",
                  value: "LN",
                },
              ],
            },
            {
              name: "configurator.shaft.step.2.substep.2.tile.2.title",

              cstics: [
                {
                  csticName: "VLMAX_01A_078",
                  value: "LS",
                },
              ],
            },
          ],

          tilesPerRow: 3,
          csticsToReset: ["VLMAX_01A_078"],
          visible: true,
        },
        {
          type: SubstepType.ShaftMachining,
          name: "configurator.shaft.step.2.substep.3.title",

          groupedTiles: [
            {
              name: "configurator.shaft.step.2.substep.3.title",
              tiles: [
                {
                  name: "configurator.shaft.step.2.substep.3.tile.000.title",
                  description: "configurator.shaft.step.2.substep.3.tile.000.description",
                  info: "configurator.shaft.step.2.substep.3.tile.000.infoPopup.inch.left",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.000.image.left",
                  cstics: [
                    {
                      csticName: "VBEAL_078",
                      value: "000",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.005.title",
                  description: "configurator.shaft.step.2.substep.3.tile.005.description",
                  info: "configurator.shaft.step.2.substep.3.tile.005.infoPopup.left.inch",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.005.image.left",
                  cstics: [
                    {
                      csticName: "VBEAL_078",
                      value: "005",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.001.title",
                  description: "configurator.shaft.step.2.substep.3.tile.001.description",
                  info: "configurator.shaft.step.2.substep.3.tile.001.infoPopup.left.inch",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.001.image.left",
                  cstics: [
                    {
                      csticName: "VBEAL_078",
                      value: "001",
                    },
                  ],
                },
              ],
              visible: true,
            },
            {
              name: "configurator.shaft.step.2.substep.3.title",
              tiles: [
                {
                  name: "configurator.shaft.step.2.substep.3.tile.900.title",
                  description: "configurator.shaft.step.2.substep.3.tile.900.description",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.900.image",
                  cstics: [
                    {
                      csticName: "VBEAM_078",
                      value: "900",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.980.title.inch",
                  description: "configurator.shaft.step.2.substep.3.tile.980.description.inch",
                  info: "configurator.shaft.step.2.substep.3.tile.980.infoPopup.inch",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.980.image",
                  cstics: [
                    {
                      csticName: "VBEAM_078",
                      value: "980",
                    },
                  ],
                },
              ],
              visible: true,
            },
            {
              name: "configurator.shaft.step.2.substep.3.title",
              tiles: [
                {
                  name: "configurator.shaft.step.2.substep.3.tile.000.title",
                  description: "configurator.shaft.step.2.substep.3.tile.000.description",
                  info: "configurator.shaft.step.2.substep.3.tile.000.infoPopup.inch.right",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.000.image.right",
                  cstics: [
                    {
                      csticName: "VBEAR_078",
                      value: "000",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.005.title",
                  description: "configurator.shaft.step.2.substep.3.tile.005.description",
                  info: "configurator.shaft.step.2.substep.3.tile.005.infoPopup.right.inch",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.005.image.right",
                  cstics: [
                    {
                      csticName: "VBEAR_078",
                      value: "005",
                    },
                  ],
                },
                {
                  name: "configurator.shaft.step.2.substep.3.tile.001.title",
                  description: "configurator.shaft.step.2.substep.3.tile.001.description",
                  info: "configurator.shaft.step.2.substep.3.tile.001.infoPopup.right.inch",
                  imageUrl: "configurator.shaft.step.2.substep.3.tile.001.image.right",
                  cstics: [
                    {
                      csticName: "VBEAR_078",
                      value: "001",
                    },
                  ],
                },
              ],
              visible: true,
            },
          ],

          tilesPerRow: 3,
          csticsToReset: ["VBEAL_078, VBEAM_078, VBEAR_078"],
          visible: true,
        },
      ],
    },
    {
      name: "configurator.shaft.step.4.title",
      enabled: true,
      resetLabel: "",
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SummaryPage,
          visible: true,

          info: [
            {
              condition: () => {
                return userIsCustomer();
              },
              value: "configurator.shaft.summary.message.designation.customer",
            },
            {
              condition: () => {
                return !userIsCustomer();
              },
              value: "configurator.shaft.summary.message.designation.public",
            },
          ],
        },
      ],
    },
  ],
};
